import { atLeast, type Role } from "@/lib/types";
import { sameNumber, toE164 } from "@/lib/phone-number";
import { formatPhoneNumber } from "@/lib/utils";

/**
 * Blocked callers (ADR-159 in platform): the callers a tenant takes no calls from, for an hour,
 * eight hours, a day or for good. The switch turns a blocked number away with a busy tone, and a
 * hidden caller with a short message, before any phone rings.
 *
 * A rep blocks for up to a day; blocking for good, and unblocking early, take a supervisor or
 * above. Callers who hide their number are blocked for a day at most, by anyone. core-api holds
 * every one of these rules: the console only offers what the server would accept.
 */

export type BlockDuration = "1h" | "8h" | "1d" | "forever";

export interface BlockedCaller {
  id: number;
  tenant_id: number;
  /** E.164, or `anonymous` for the block on hidden callers. */
  number: string;
  hidden: boolean;
  note: string | null;
  lead_id: string | null;
  created_by: number | null;
  /** The name of the person who blocked it, while their account exists. */
  blocked_by: string | null;
  created_at: string;
  /** When the block ends; null blocks for good. */
  expires_at: string | null;
}

export interface BlockCallerInput {
  number?: string;
  hidden?: boolean;
  duration: BlockDuration;
  note?: string;
  lead_id?: string;
  /** The phone's own leg of the call the block is made from. */
  call_id?: string;
}

/** Who a block is on: a number, or every caller who hides theirs. */
export interface BlockTarget {
  /** The number as the call or the record has it; null for hidden callers. */
  number: string | null;
  hidden: boolean;
  /** The contact the caller is, when the console knows. */
  leadId?: string | null;
  /** Their name, when the console knows it, for the dialog's title. */
  name?: string | null;
  /**
   * The phone's own call the block is made from, ringing or up. core-api keeps that call as
   * blocked (no missed call, "Blocked" in the call report), and takes a caller still waiting in a
   * line off it, so the next rep is not rung for them.
   */
  callId?: string | null;
}

export const DURATION_LABELS: Record<BlockDuration, string> = {
  "1h": "1 hour",
  "8h": "8 hours",
  "1d": "1 day",
  forever: "Forever",
};

const LENGTH_MS: Record<Exclude<BlockDuration, "forever">, number> = {
  "1h": 3_600_000,
  "8h": 8 * 3_600_000,
  "1d": 24 * 3_600_000,
};

/** The caller id a carrier gives a caller who hides their number. */
export const HIDDEN_NUMBER = "anonymous";

/** A screen showing a block asks again this long after it ends, when the server's clock has
 *  passed the end too. */
const END_SLACK_MS = 2_000;
/** And no sooner than this, should the server still have it. */
const ASK_AGAIN_MS = 5_000;

/**
 * Whether a call's number is a hidden caller's: `anonymous`, in any case, the caller id the
 * carrier gives them. Nothing else is. A missing number is a gap in the data, not a caller who
 * hid theirs, and blocking hidden callers for it would turn away every one of them on a guess;
 * `unknown` and `restricted` are not either, since the switch's block matches `anonymous` only.
 */
export function isHiddenCaller(number: string | null | undefined): boolean {
  return (number ?? "").trim().toLowerCase() === HIDDEN_NUMBER;
}

/**
 * A caller as core-api keeps them and a lookup names them (`GET /blocked-callers?number=`): the
 * number in E.164, or `anonymous` for hidden callers. Null for a number nobody can block.
 */
export function blockedCallerKey(target: Pick<BlockTarget, "number" | "hidden">): string | null {
  return target.hidden ? HIDDEN_NUMBER : toE164(target.number);
}

/** The lengths this person may choose for this caller, shortest first. */
export function durationsFor(role: Role | undefined, hidden: boolean): BlockDuration[] {
  const temporary: BlockDuration[] = ["1h", "8h", "1d"];
  return !hidden && atLeast(role, "supervisor") ? [...temporary, "forever"] : temporary;
}

/** Whether this person may end a block early. */
export function canUnblock(role: Role | undefined): boolean {
  return atLeast(role, "supervisor");
}

/** The block in force on this caller, if the list has one. */
export function blockFor(
  list: readonly BlockedCaller[] | undefined,
  target: Pick<BlockTarget, "number" | "hidden">,
  now: number = Date.now(),
): BlockedCaller | undefined {
  return list?.find(
    (b) =>
      (b.expires_at === null || new Date(b.expires_at).getTime() > now) &&
      (target.hidden ? b.hidden : !b.hidden && sameNumber(b.number, target.number)),
  );
}

/**
 * How long until the first of these blocks ends: a screen that shows them asks again then, so
 * "Blocked" goes when the block does, not at the next refetch. False when none of them ends.
 */
export function untilFirstEnds(list: readonly BlockedCaller[] | undefined, now: number = Date.now()): number | false {
  const ends = (list ?? []).flatMap((b) => (b.expires_at === null ? [] : [Date.parse(b.expires_at)]));
  if (ends.length === 0) return false;
  return Math.max(Math.min(...ends) - now + END_SLACK_MS, ASK_AGAIN_MS);
}

/** Whether a block already in force lasts at least as long as one of `duration` made now. */
export function outlasts(block: BlockedCaller, duration: BlockDuration, now: number = Date.now()): boolean {
  if (block.expires_at === null) return true;
  if (duration === "forever") return false;
  return new Date(block.expires_at).getTime() >= now + LENGTH_MS[duration];
}

/** The caller as a person reads them: the number, or "Hidden callers". */
export function callerLabel(block: Pick<BlockedCaller, "number" | "hidden">): string {
  return block.hidden ? "Hidden callers" : formatPhoneNumber(block.number);
}

/** "For good", or when the block ends: the time today, a weekday and time this week, else a date. */
export function untilText(block: Pick<BlockedCaller, "expires_at">, now: Date = new Date()): string {
  if (block.expires_at === null) return "For good";
  const ends = new Date(block.expires_at);
  const time = ends.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  if (ends.toDateString() === now.toDateString()) return `Until ${time}`;
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  if (ends.toDateString() === tomorrow.toDateString()) return `Until tomorrow, ${time}`;
  const day = ends.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  return `Until ${day}, ${time}`;
}

/** The same, inside a sentence: "until 3:15 PM", "for good". */
export function untilPhrase(block: Pick<BlockedCaller, "expires_at">, now: Date = new Date()): string {
  const text = untilText(block, now);
  return text.charAt(0).toLowerCase() + text.slice(1);
}
